import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';

import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';

import {
  COLOR_PRIMARY,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_GREEN,
  COLOR_RED,
  COLOR_E,
  Top10ManagerDummyData,
  exchangeRates,
} from 'src/const';

import { ListingsProp } from 'src/interfaces/iproptypes';
import { Host, Listing } from 'src/interfaces/idashboard';
import {
  getVRMs,
  convertCurrency,
  formatCurrency,
  convertListingsDictToArray,
} from 'src/utils/functions';

import { InfoTooltip } from '../Tooltips';
import { TooltipCard } from '.';
import UnlockButton from '../CTA/UnlockButton';

const cellCol = 'rgba(201, 207, 218, 0.15)';

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

interface Top10ManagerCardProps {
  isDisabled: boolean;
  listings: any;
  currentMarket: string;
  currency: string;
  multipleMarkersCallLoading: Boolean;
  handleOnSampleClick: () => void;
}

const Top10ManagerCard = ({
  isDisabled,
  listings,
  currentMarket,
  currency,
  multipleMarkersCallLoading,
  handleOnSampleClick,
}: Top10ManagerCardProps) => {
  const { colorPrimary, barColorPrimary } = loadingStyle({ backgroundColor: COLOR_PRIMARY });

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  let data = Top10ManagerDummyData;
  let allData = Top10ManagerDummyData;

  let processedData: Host[] = [];
  let allProcessedData: Host[] = [];

  if (!isDisabled && !listings.loading && Object.keys(listings.data).length > 0) {
    const allDataObject = getVRMs(convertListingsDictToArray(listings.data) as Listing[]);
    processedData = allDataObject.top10ManagerArray;
    allProcessedData = allDataObject.allManagers;
    // console.log('Processed Data: ', allProcessedData);

    data = processedData.map(data => {
      return {
        id: data.hostId,
        name: data.name,
        revenue: data.revenue,
        listings: data.totalNumberOfListings,
        image: data.listingImage,
        totalRating: data.totalRating,
      };
    });

    allData = allProcessedData.map(data => {
      return {
        id: data.hostId,
        name: data.name,
        revenue: data.revenue,
        listings: data.totalNumberOfListings,
        image: data.listingImage,
        totalRating: data.totalRating,
      };
    });
  }

  const listViewCols: GridColDef[] = [
    { field: 'listingID', headerName: 'Listing ID', width: 200, hide: true },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            position: 'relative',
            fontWeight: 400,
            fontSize: '16px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            border: 'none',
          }}
        >
          <a href={`https://www.airbnb.com/users/show/${params.row.id}`} rel="nofollow" target="_blank">
            <img
              src={params.row.image}
              alt={''}
              style={{
                height: '40px',
                width: '40px',
                objectFit: 'cover',
                borderRadius: '4px',
              }}
            />
          </a>
          <abbr
            title={params.row.name}
            tabIndex={0}
            style={{ textDecoration: 'none', cursor: 'help' }}
          >
            {params.row.name}
          </abbr>
        </Box>
      ),
    },
    {
      field: 'totalRevenue',
      headerName: 'Total Revenue (Last 12 Months)',
      flex: 1,
      minWidth: 100,
      valueGetter: params => {
        return Number(params.row.revenue);
      },
      renderCell: (params: GridRenderCellParams) => (
        <>
          {exchangeRates[currency].symbol}{' '}
          {formatCurrency(convertCurrency(currency, params.row.revenue) + '')}
        </>
      ),
    },
    {
      field: 'totalListings',
      headerName: 'Total number of listings in this city',
      flex: 1,
      minWidth: 100,
      valueGetter: params => {
        return params.row.listings;
      },
      renderCell: (params: GridRenderCellParams) => <Typography>{params.row.listings}</Typography>,
    },
    {
      field: 'avgRating',
      headerName: 'Average User Rating',
      flex: 1,
      minWidth: 70,
      valueGetter: params => {
        return Number(params.row.totalRating / params.row.listings);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{(params.row.totalRating / params.row.listings).toFixed(1)}</Typography>
      ),
    },
  ];

  return (
    <>
      <Card>
        <CardContent>
          {multipleMarkersCallLoading && !isDisabled && (
            <Grid item sx={{ width: '100%', marginBottom: '15px', color: COLOR_PRIMARY }}>
              <LinearProgress
                classes={{ colorPrimary: colorPrimary, barColorPrimary: barColorPrimary }}
              />
            </Grid>
          )}

          <Container>
            <Grid container item xs={12} sx={{ marginTop: '20px' }}>
              <Typography
                variant="body2"
                component="h2"
                sx={{
                  fontWeight: 400,
                  fontSize: '17px',
                }}
              >
                Top Vacation Rental Managers in
                {
                  isDisabled? " Demo Market: City of Brighton and Hove" : " this market "
                }
              </Typography>
            </Grid>

            <TableContainer
              sx={{
                position: 'relative',
                width: {
                  xs: '90vw',
                  sm: '100%',
                },
              }}
            >
              <Table
                aria-label="Top 10 rental managers in the market"
                sx={{
                  marginTop: '25px',
                  minWidth: 650,
                  overflow: 'scroll',
                  // filter: isDisabled ? 'blur(4px)' : 'none',        
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderTop: '2px solid #e8e8e866' }}>
                    <TableCell
                      sx={{
                        borderLeft: '2px solid #e8e8e866',
                        borderRight: '2px solid #e8e8e866',
                        fontWeight: 500,
                        fontSize: '16px',
                        minWidth: '120px',
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 500, fontSize: '16px', borderRight: '2px solid #e8e8e866' }}
                    >
                      Total Revenue (Last 12 Months)
                    </TableCell>
                    <InfoTooltip
                      title={
                        <TooltipCard content={'Total Number of Listings in ' + currentMarket} />
                      }
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: '16px',
                          borderRight: '2px solid #e8e8e866',
                        }}
                      >
                        Total Number of Listings in{' '}
                        {currentMarket.length > 15
                          ? currentMarket.substr(0, 12) + '...'
                          : currentMarket}
                      </TableCell>
                    </InfoTooltip>
                    <TableCell
                      sx={{ fontWeight: 500, fontSize: '16px', borderRight: '2px solid #e8e8e866' }}
                    >
                      Average User Rating
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((data, idx) => (
                    <TableRow
                      key={'TableTop10' + idx}
                      sx={{
                        '&:nth-child(2n-1)': {
                          backgroundColor: cellCol,
                        },
                        '&': {
                          borderBottom: '2px solid #e8e8e866 !important',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: 'relative',
                          fontWeight: 400,
                          fontSize: '16px',
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center',
                          border: 'none',
                        }}
                      >
                        <a href={`https://www.airbnb.com/users/show/${data.id}`} rel="nofollow" target="_blank">
                          <img
                            src={data.image}
                            alt={''}
                            style={{
                              height: '40px',
                              width: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                            }}
                          />
                        </a>
                        <abbr
                          title={data.name}
                          tabIndex={0}
                          style={{ textDecoration: 'none', cursor: 'help' }}
                        >
                          {data.name}
                        </abbr>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {exchangeRates[currency].symbol}{' '}
                        {formatCurrency(convertCurrency(currency, data.revenue) + '')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {data.listings}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {(data.totalRating / data.listings).toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {isDisabled && (
                <UnlockButton handleOnSampleClick={handleOnSampleClick} />
              )}

            </TableContainer>

            {!isDisabled && (
              <Button
                sx={{ float: 'right', marginTop: '20px', marginBottom: '20px' }}
                onClick={() => setModalOpen(true)}
              >
                See All
              </Button>
            )}
          </Container>
        </CardContent>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <Container>
          <Box
            sx={{
              background: '#fff',
              height: '80vh',
              width: '90vw',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <DataGrid
              rows={allData}
              columns={listViewCols}
              checkboxSelection={false}
              components={{
                Toolbar: () => (
                  <GridToolbarContainer>
                    <GridToolbarExport />
                  </GridToolbarContainer>
                ),
              }}
              disableSelectionOnClick={true}
            />
          </Box>
        </Container>
      </Modal>
    </>
  );
};

// export default Top10ManagerCard;

export default React.memo(Top10ManagerCard);
